import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from '@tanstack/react-router';
import type { MemberRole } from '@app/shared';
import {
  Banner,
  Button,
  Cluster,
  FormField,
  Heading,
  Link,
  PageHeader,
  SelectMenu,
  Stack,
  StatusBadge,
  Surface,
  Text,
  TextInput,
} from '../ui';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/queryKeys';
import { useAuth, useWorkspaceId } from '../lib/auth';
import { RunStatusBadge } from '../components/RunStatusBadge';
import { PollerConfigSection } from '../components/PollerConfigSection';
import { PollerRunsSection } from '../components/PollerRunsSection';

const ROLE_OPTIONS = [
  { value: 'member', label: 'Member' },
  { value: 'owner', label: 'Owner' },
];

/** The Admin surfaces (§14 SHOULD) planned for a follow-up — their read/write APIs aren't
 * exposed in v1 yet, so they read as "Coming soon" rows. */
const WORKSPACE_CONTROLS = [
  {
    title: 'Kill switch',
    note: 'Enforced server-side in the gateway today (§7.7); the toggle appears here when the API lands.',
  },
  {
    title: 'Audit log',
    note: 'A searchable view of gateway decisions and admin actions.',
  },
  {
    title: 'Failed jobs',
    note: 'Inspect and retry jobs that exhausted their attempts.',
  },
];

/**
 * Admin (§14, SHOULD): members & roles (invite an existing user), and a NOTE for the
 * workspace kill switch / audit log / failed-job views (those APIs are not exposed
 * in v1 — flagged for a follow-up). Invite is owner-only (server-enforced).
 */
export function AdminPage() {
  const workspaceId = useWorkspaceId();
  const { workspaces } = useAuth();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('member');
  const [banner, setBanner] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const invite = useMutation({
    mutationFn: () => api.invite(workspaceId, { email, role }),
    onSuccess: () => {
      setBanner({ kind: 'success', text: `Invited ${email} as ${role}.` });
      setEmail('');
    },
    onError: (err) =>
      setBanner({ kind: 'error', text: err instanceof ApiError ? err.message : 'Invite failed.' }),
  });

  const myRole = workspaces.find((w) => w.id === workspaceId)?.role ?? 'member';

  return (
    <Stack gap="6">
      <PageHeader title="Admin" description="Members, roles and workspace controls" />

      {banner && (
        <Banner
          tone={banner.kind === 'success' ? 'success' : 'danger'}
          onDismiss={() => setBanner(null)}
          data-testid="admin-banner"
        >
          {banner.text}
        </Banner>
      )}

      <Surface bordered radius="lg" padding="5">
        <Stack gap="4">
          <Cluster gap="2" align="center">
            <Heading level={2} size="md">
              Your role
            </Heading>
            <RunStatusBadge status={myRole} />
          </Cluster>

          <Stack gap="1">
            <Heading level={3} size="sm">
              Invite a member
            </Heading>
            <Text size="sm" tone="secondary">
              v1 invite adds an existing platform user by email (D9). Owner-only.
            </Text>
          </Stack>

          <Cluster gap="4" align="end" wrap>
            <div style={{ flex: '1 1 16rem', minWidth: 0 }}>
              <FormField label="Email">
                <TextInput
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-email"
                />
              </FormField>
            </div>
            <Stack gap="2">
              <Text size="sm" weight="medium">
                Role
              </Text>
              <SelectMenu
                value={role}
                onChange={(value) => setRole(value as MemberRole)}
                options={ROLE_OPTIONS}
                label="Role"
                minWidth="10rem"
                data-testid="admin-role"
              />
            </Stack>
            <Button
              onClick={() => invite.mutate()}
              disabled={!email}
              loading={invite.isPending}
              data-testid="admin-invite"
            >
              Invite
            </Button>
          </Cluster>
        </Stack>
      </Surface>

      <Surface bordered radius="lg" padding="5">
        <Stack gap="4">
          <Stack gap="1">
            <Heading level={2} size="md">
              Workspace controls
            </Heading>
            <Text size="sm" tone="secondary">
              These Admin surfaces (§14 SHOULD) are planned for a follow-up work package — their
              read/write APIs aren&apos;t exposed in v1 yet.
            </Text>
          </Stack>

          <Stack gap="2">
            {WORKSPACE_CONTROLS.map((item) => (
              <Surface key={item.title} tone="subtle" bordered radius="md" padding="4">
                <Cluster gap="3" align="center" justify="between" wrap={false}>
                  <Stack gap="1">
                    <Text weight="semibold">{item.title}</Text>
                    <Text size="sm" tone="secondary">
                      {item.note}
                    </Text>
                  </Stack>
                  <StatusBadge tone="neutral" label="Coming soon" />
                </Cluster>
              </Surface>
            ))}
          </Stack>
        </Stack>
      </Surface>

      {/* Operator-only entry point to the cross-tenant Platform dashboard (#332), plus the
          global email-poller settings + recent-activity history. All three render nothing for
          non-operators (their reads 403), so normal admins never see them. */}
      <PlatformDashboardCard />
      <PollerConfigSection />
      <PollerRunsSection />
    </Stack>
  );
}

/**
 * Operator-only card linking to the cross-tenant Platform dashboard (`/app/admin/platform`,
 * #332). Self-hides for non-operators using the SAME operator probe the page uses — we attempt
 * the operator-gated `getPlatformStats` read and render nothing on any failure (403 non-operator,
 * 401 anonymous, or a transient error), so the link's existence never leaks. Mirrors the
 * try-load-then-hide gate of the poller sections.
 */
function PlatformDashboardCard() {
  const query = useQuery({
    queryKey: qk.platformStats(),
    queryFn: () => api.getPlatformStats(),
    retry: false,
  });

  if (query.isPending || query.isError || !query.data) return null;

  return (
    <Surface bordered radius="lg" padding="5" data-testid="admin-platform-card">
      <Cluster gap="3" align="center" justify="between" wrap>
        <Stack gap="1" style={{ flex: '1 1 20rem', minWidth: 0 }}>
          <Cluster gap="2" align="center">
            <Heading level={2} size="md">
              Platform dashboard
            </Heading>
            <StatusBadge tone="brand" label="Operator" />
          </Cluster>
          <Text size="sm" tone="secondary">
            Cross-tenant totals across every workspace — users, workspaces, runs and total spend.
          </Text>
        </Stack>
        <Link
          as={RouterLink}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          {...({ to: '/app/admin/platform' } as any)}
          data-testid="admin-platform-link"
        >
          Open platform dashboard →
        </Link>
      </Cluster>
    </Surface>
  );
}
